import { Fragment, useRef, useState, type KeyboardEvent } from "react";
import { splitBracketGaps } from "../../shared/prompts/gaps";
import { SLOT_META, type FilledPart, type SlotKey } from "../../shared/prompts/templates";
import { elementById, focusElement, inputValue, selectText } from "../lib/dom";

export type EditableSlot = Exclude<SlotKey, "platform">;

interface Editing {
  key: string;
  slot: EditableSlot;
  original: string;
}

/** Text with its "[bracketed]" gaps drawn in `gapClass`. By default, gaps left for the AI tool. */
export function BracketText({
  text,
  gapClass = "slot slot-hand",
}: {
  text: string;
  gapClass?: string;
}) {
  return splitBracketGaps(text).map((part, i) =>
    part.gap ? (
      <span key={i} className={gapClass}>
        {part.text}
      </span>
    ) : (
      <Fragment key={i}>{part.text}</Fragment>
    ),
  );
}

/**
 * The filled prompt with every slot editable where it sits: a slot is a button that turns
 * into an input. Enter or blur keeps the value, Escape restores it.
 */
export function InlinePrompt({
  parts,
  valueOf,
  onEdit,
  onCommit,
  enhanced,
  flush = (fn) => fn(),
}: {
  parts: readonly FilledPart[];
  /** The visitor's raw value for a slot, as typed. */
  valueOf: (slot: EditableSlot) => string;
  onEdit: (slot: EditableSlot, value: string) => void;
  onCommit: (slot: EditableSlot) => void;
  /** False for the server render and hydration, where slots render as plain highlights. */
  enhanced: boolean;
  /**
   * react-dom's flushSync, passed in by the client-only island so the Worker bundle does not
   * pull in React's browser renderer. It puts the input in the DOM inside the tap, so iOS
   * opens the keyboard on focus.
   */
  flush?: (fn: () => void) => void;
}) {
  const [editing, setEditing] = useState<Editing | null>(null);
  const current = useRef<Editing | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const start = (key: string, slot: EditableSlot) => {
    if (current.current) onCommit(current.current.slot);
    const next = { key, slot, original: valueOf(slot) };
    current.current = next;
    flush(() => setEditing(next));
    focusElement(inputRef.current);
    selectText(inputRef.current);
  };

  const finish = (refocus: boolean) => {
    const done = current.current;
    if (!done) return;
    current.current = null;
    setEditing(null);
    onCommit(done.slot);
    if (refocus) {
      // After React swaps the input back to the slot button.
      setTimeout(() => focusElement(elementById(`pb-gap-${done.key}`)), 0);
    }
  };

  const onInputKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      finish(true);
    } else if (event.key === "Escape") {
      event.preventDefault();
      if (current.current) onEdit(current.current.slot, current.current.original);
      finish(true);
    }
  };

  const seen: Partial<Record<EditableSlot, number>> = {};
  return parts.map((part, index) => {
    if (part.slot === undefined || part.slot === "platform") {
      return <BracketText key={index} text={part.text} />;
    }
    const slot = part.slot;
    const occurrence = (seen[slot] ?? 0) + 1;
    seen[slot] = occurrence;
    const key = `${slot}-${occurrence}`;
    const meta = SLOT_META[slot];

    if (editing?.key === key) {
      const value = valueOf(slot);
      return (
        <input
          key={key}
          ref={inputRef}
          className="gap-input"
          aria-label={meta.label}
          value={value}
          placeholder={meta.placeholder}
          size={Math.max(value.length, meta.placeholder.length) + 1}
          enterKeyHint="done"
          autoComplete={slot === "brand" ? "organization" : "off"}
          onChange={(event) => onEdit(slot, inputValue(event))}
          onKeyDown={onInputKeyDown}
          onBlur={() => {
            if (current.current?.key === key) finish(false);
          }}
        />
      );
    }

    const className = part.filled ? "gap gap-filled" : "gap";
    if (!enhanced) {
      return (
        <span key={key} className={className}>
          {part.text}
        </span>
      );
    }
    return (
      <span
        key={key}
        id={`pb-gap-${key}`}
        role="button"
        tabIndex={0}
        className={className}
        aria-label={`${meta.label}: ${part.text}`}
        // While another slot is open, keep its input from blurring and reflowing the text
        // under the pointer before this click lands; start() commits it instead.
        onMouseDown={(event) => {
          if (current.current) event.preventDefault();
        }}
        onClick={() => start(key, slot)}
        onKeyDown={(event) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          start(key, slot);
        }}
      >
        {part.text}
      </span>
    );
  });
}
